/**
 * LocationGuard Component
 * Ensures user has granted location permission before accessing protected routes
 * Redirects to home page if no location is available
 */

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../context/StoreContext';

export default function LocationGuard({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Defensive: `useStore` can sometimes be unavailable during HMR or if provider mounting fails.
  // Wrap in try/catch and fall back to localStorage to avoid crashing the entire app.
  let hasLocation = false;
  let isLoading = true;

  try {
    const storeCtx = useStore();
    hasLocation = !!storeCtx.hasLocation;
    isLoading = !!storeCtx.isLoading;
  } catch (err) {
    // Fallback: infer from localStorage (best-effort) and avoid throwing
    // This prevents the whole app from crashing if the provider is temporarily missing.
    try {
      const saved = localStorage.getItem('tcr_user_location');
      hasLocation = !!saved && saved !== 'null' && saved !== 'undefined';
    } catch (e) {
      hasLocation = false;
    }
    isLoading = false;

    if (process.env.NODE_ENV !== 'production') {
      // Helpful dev-time warning (non-blocking)
      // eslint-disable-next-line no-console
      console.warn('LocationGuard: useStore() unavailable — using localStorage fallback', err && err.message);
    }
  }

  useEffect(() => {
    // Wait for context to finish loading from localStorage
    if (isLoading) return;

    // If no location and not on home page, redirect to home
    if (!hasLocation && location.pathname !== '/') {
      console.log('🔒 No location permission, redirecting to home');
      navigate('/', { replace: true });
    }
  }, [hasLocation, isLoading, location.pathname, navigate]);

  // Show nothing while checking (prevents flash)
  if (isLoading) {
    return null;
  }

  // If on home page, always render children (home handles its own location modal)
  if (location.pathname === '/') {
    return children;
  }

  // If no location, don't render protected content (redirect will happen)
  if (!hasLocation) {
    return null;
  }

  return children;
}
